import { FOCUS_RING_INSET, Icon, Text } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { ProficiencyLevel, SkillItem } from '@/types/performance-profile';

import { useGetProficiencyLevels } from '@/api/performance-profile';
import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { ProficiencyLevelBars } from '@/components/ProficiencyIcon/ProficiencyLevelBars';
import { resolveProficiencyLevelColor } from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { orderProficiencyLevels } from '@/utils/proficiencyLevel';

const RETIRED_BADGE = 'ds-px-md ds-py-xs text-caption bg-growth-tint text-obj-gray rounded-full';
const ROW_ACTION = 'hover:bg-base-50 rounded-full p-1 transition-colors';

const ProficiencyIconCell = ({ skillId, level }: { skillId: string; level: ProficiencyLevel }) => {
    const { data: levels } = useGetProficiencyLevels();

    if (!level) {
        return <span className={'text-obj-gray'}>—</span>;
    }

    const iconEntry = level.icon ? getIconEntry(level.icon) : undefined;
    const bestFirst = orderProficiencyLevels(levels ?? []).map(({ id }) => id);
    const levelIndex = bestFirst.indexOf(level.id);
    // A level that carries no colour of its own takes the one its rank is shown in, and a stored
    // Tailwind class is turned into a colour value the bars can actually paint with.
    const levelColor = resolveProficiencyLevelColor({ iconColor: level.iconColor, levelIndex });

    return (
        <div className={'flex flex-col items-center gap-1'}>
            <ProficiencyIcon
                iconUrl={level.iconUrl}
                iconEntry={iconEntry}
                color={levelColor}
                className={'h-4 w-4'}
                dataTestId={`skillset-skill-${skillId}-proficiency-icon`}
            />
            <ProficiencyLevelBars
                levelIndex={levelIndex}
                totalLevels={bestFirst.length}
                color={levelColor}
                dataTestId={`skillset-skill-${skillId}-proficiency-bars`}
            />
        </div>
    );
};

interface SkillsetSkillRowProps {
    skill: SkillItem;
    onEdit: (skill: SkillItem) => void;
    onViewAssessmentHistory: (skill: SkillItem) => void;
}

/** One competency of a category: its level, its name, what became of it, and the two actions it
 * offers. The whole row opens the history; the actions inside it stop there. */
export const SkillsetSkillRow = ({
    skill,
    onEdit,
    onViewAssessmentHistory,
}: SkillsetSkillRowProps) => {
    const { t } = useTranslation();
    const isRetired = skill.isDeactivated || skill.isDeleted;

    return (
        <div
            role={'button'}
            tabIndex={0}
            aria-label={t('performanceProfile.skillsetAssessment.openHistoryFor', {
                skill: skill.name,
            })}
            className={`ds-px-lg ds-py-lg hover:bg-growth-tint flex cursor-pointer items-center justify-between bg-white ${FOCUS_RING_INSET}`}
            data-testid={`skillset-skill-${skill.id}`}
            onClick={() => onViewAssessmentHistory(skill)}
            onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') {
                    return;
                }
                // Espace ferait defiler la page, Entree ne fait rien sur un `div` : les deux sont
                // pris en charge.
                event.preventDefault();
                onViewAssessmentHistory(skill);
            }}
        >
            <div className={'flex items-center gap-3'}>
                <ProficiencyIconCell
                    skillId={skill.id}
                    level={skill.proficiency}
                />
                <div className={'flex flex-col gap-1'}>
                    <div className={'flex items-center gap-2'}>
                        <Text
                            weight={'semibold'}
                            className={
                                isRetired
                                    ? 'text-body-sm text-obj-gray line-through'
                                    : 'text-body-sm text-navy'
                            }
                        >
                            {skill.name}
                        </Text>
                        {skill.isDeleted && (
                            <span
                                className={RETIRED_BADGE}
                                data-testid={`skillset-skill-${skill.id}-deleted`}
                            >
                                {t('common.deleted')}
                            </span>
                        )}
                        {skill.isDeactivated && !skill.isDeleted && (
                            <span
                                className={RETIRED_BADGE}
                                data-testid={`skillset-skill-${skill.id}-deactivated`}
                            >
                                {t('common.deactivated')}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className={'flex items-center gap-5'}>
                <button
                    type={'button'}
                    onClick={(e) => {
                        e.stopPropagation();
                        onViewAssessmentHistory(skill);
                    }}
                    aria-label={t(
                        'performanceProfile.skillsetAssessment.assessmentHistoryModal.subtitle',
                    )}
                    data-testid={`skillset-skill-${skill.id}-history-button`}
                    className={ROW_ACTION}
                >
                    <Icon
                        name={'time-clock'}
                        size={'sm'}
                        colorClassName={'text-obj-gray'}
                    />
                </button>
                {isRetired ? (
                    <span className={'w-6'} />
                ) : (
                    <button
                        type={'button'}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(skill);
                        }}
                        aria-label={t('common.edit')}
                        data-testid={`skillset-skill-${skill.id}-edit-button`}
                        className={ROW_ACTION}
                    >
                        <Icon
                            name={'money-icon_pencil'}
                            size={'sm'}
                            colorClassName={'text-obj-gray'}
                        />
                    </button>
                )}
            </div>
        </div>
    );
};
